import {
  type AtticErrorFragment,
  useAtticReserveSetMutation,
  useAtticStockAdjustMutation,
  useAtticStockLookupQuery,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { useNotifier } from "@dashboard/hooks/useNotifier/useNotifier";
import { getMutationState } from "@dashboard/misc";
import { useState } from "react";
import { useIntl } from "react-intl";

import { AtticAdjustDialog } from "../components/AtticStockLookupPage/AtticAdjustDialog";
import { AtticReserveDialog } from "../components/AtticStockLookupPage/AtticReserveDialog";
import AtticStockLookupPage from "../components/AtticStockLookupPage/AtticStockLookupPage";
import { stockMessages } from "../messages";
import { atticStockLookupUrl, type AtticStockLookupUrlQueryParams } from "../urls";
import { normalizeSku } from "../utils";

type Dialog = "reserve" | "adjust" | null;

export const AtticStockLookupView = ({ params }: { params: AtticStockLookupUrlQueryParams }) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();
  const sku = normalizeSku(params.sku ?? "");
  const [dialog, setDialog] = useState<Dialog>(null);
  const [errors, setErrors] = useState<AtticErrorFragment[]>([]);
  const { data, loading, error } = useAtticStockLookupQuery({
    displayLoader: true,
    variables: { sku },
    skip: !sku,
  });
  const stock = data?.atticStock;
  // Every lot is in the store's one currency today, so the first unit's is it.
  const currency = stock?.units[0]?.lot.currency ?? "USD";

  const close = () => {
    setDialog(null);
    setErrors([]);
  };
  // Both payloads hand back the position as it now stands; it is written to
  // the lookup's own cache entry so the page refreshes without a refetch.
  const [setReserve, reserveOpts] = useAtticReserveSetMutation({
    disableNestedErrorHandling: true,
    refetchQueries: ["AtticStockLookup"],
    onCompleted: result => {
      const refused = result.atticReserveSet?.errors ?? [];

      setErrors(refused);

      if (!refused.length) {
        notify({ status: "success", text: intl.formatMessage(stockMessages.reserveSaved) });
        close();
      }
    },
  });
  const [adjust, adjustOpts] = useAtticStockAdjustMutation({
    disableNestedErrorHandling: true,
    refetchQueries: ["AtticStockLookup"],
    onCompleted: result => {
      const refused = result.atticStockAdjust?.errors ?? [];

      setErrors(refused);

      if (!refused.length) {
        notify({ status: "success", text: intl.formatMessage(stockMessages.adjustSaved) });
        close();
      }
    },
  });

  return (
    <>
      <AtticStockLookupPage
        sku={sku}
        stock={!sku ? null : loading && !data ? undefined : (stock ?? null)}
        loadFailed={!!error && !data}
        currency={currency}
        onLookup={next => navigate(atticStockLookupUrl(next ? { sku: next } : undefined))}
        onSetReserve={() => setDialog("reserve")}
        onAdjust={() => setDialog("adjust")}
      />
      {stock && (
        <>
          <AtticReserveDialog
            open={dialog === "reserve"}
            reserved={stock.reserved}
            reason={stock.reserveReason}
            onHand={stock.onHand}
            errors={errors}
            confirmButtonState={getMutationState(reserveOpts.called, reserveOpts.loading, errors)}
            onClose={close}
            onConfirm={(quantity, reason) =>
              setReserve({ variables: { sku: stock.sku, quantity, reason } })
            }
          />
          <AtticAdjustDialog
            open={dialog === "adjust"}
            defaultLocation={stock.units[0]?.location.code ?? ""}
            errors={errors}
            confirmButtonState={getMutationState(adjustOpts.called, adjustOpts.loading, errors)}
            onClose={close}
            onConfirm={input =>
              adjust({
                variables: {
                  sku: stock.sku,
                  location: input.location,
                  delta: input.delta,
                  reason: input.reason,
                  ...(input.unitCost !== null ? { unitCost: input.unitCost } : {}),
                },
              })
            }
          />
        </>
      )}
    </>
  );
};

export default AtticStockLookupView;
